import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { settingsApi } from '../services/api';

type Theme = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  theme: Theme;
  actualTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>('auto');
  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>('light');

  const loadThemeSettings = async () => {
    try {
      const settings = await settingsApi.get();
      setThemeState(settings.theme);
    } catch (error) {
      console.error('Ошибка загрузки настроек темы:', error);
    }
  };

  const applyTheme = useCallback((newTheme: Theme) => {
    const root = document.documentElement;
    
    let resolvedTheme: 'light' | 'dark';
    if (newTheme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      resolvedTheme = prefersDark ? 'dark' : 'light';
    } else {
      resolvedTheme = newTheme;
    }

    // Обновляем состояние только если тема изменилась
    setActualTheme(prev => prev !== resolvedTheme ? resolvedTheme : prev);

    // Применяем класс темы к body
    document.body.className = `theme-${resolvedTheme}`;
    
    // Устанавливаем CSS переменные
    if (resolvedTheme === 'dark') {
      root.style.setProperty('--bg-primary', '#1f2937');
      root.style.setProperty('--bg-secondary', '#111827');
      root.style.setProperty('--text-primary', '#f9fafb');
      root.style.setProperty('--text-secondary', '#d1d5db');
      root.style.setProperty('--border-color', '#374151');
      root.style.setProperty('--card-bg', '#1f2937');
    } else {
      root.style.setProperty('--bg-primary', '#ffffff');
      root.style.setProperty('--bg-secondary', '#f9fafb');
      root.style.setProperty('--text-primary', '#111827');
      root.style.setProperty('--text-secondary', '#6b7280');
      root.style.setProperty('--border-color', '#e5e7eb');
      root.style.setProperty('--card-bg', '#ffffff');
    }
  }, []);

  // Загружаем настройки темы при инициализации
  useEffect(() => {
    loadThemeSettings();
  }, []);

  // Применяем тему при изменении
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  // Слушаем изменения системной темы
  useEffect(() => {
    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        applyTheme('auto'); // Переприменяем тему
      };

      handleChange(); // Устанавливаем начальное значение
      mediaQuery.addEventListener('change', handleChange);

      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme, applyTheme]);

  const setTheme = async (newTheme: Theme) => {
    setThemeState(newTheme);
    
    // Сохраняем в настройках
    try {
      await settingsApi.update({ theme: newTheme });
    } catch (error) {
      console.error('Ошибка сохранения темы:', error);
    }
  };

  const toggleTheme = () => {
    const newTheme = actualTheme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, actualTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
